import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Grid, Tooltip, Typography, Switch, FormControlLabel } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import BlockIcon from '@mui/icons-material/Block';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { useHistory, useRouteMatch } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import InvDataGrid from '../../components/InvDataGrid';
import StatCard from '../../components/StatCard';
import InvButton from '../../components/InvButton';
import { InvTextField } from '../../components/InvField';
import FilterToolbar from '../../components/FilterToolbar';
import StatusChip from '../../components/StatusChip';
import InvItemAutocomplete from '../../components/InvItemAutocomplete';
import InvLookupAutocomplete from '../../components/InvLookupAutocomplete';
import { formatDate, formatQty, exportToCsv } from '../../utils';
import useInvRole from '../../hooks/useInvRole';
import { INV_ACTIONS } from '../../utils/inventoryRoles';
import { IPD_COLORS, switchSx } from '../../theme';
import { getQcRejections } from '../../api/qcRejections';
import { toInventoryList } from '../../api/inventory-request';
import { toInventoryPage } from '../../utils/masterEntity';
import usePoGrnLookup from '../../hooks/usePoGrnLookup';
import useItemNames from '../../hooks/useItemNames';
import useVendorLookup from '../../hooks/useVendorLookup';

// ─── Inline chip / link components ────────────────────────────────────────────

function LinkText({ onClick, disabled, children }) {
  if (disabled || !onClick) {
    return <Typography sx={{ fontSize: 13, color: IPD_COLORS.textPrimary }}>{children}</Typography>;
  }
  return (
    <Typography
      onClick={onClick}
      sx={{
        fontSize: 13,
        fontWeight: 600,
        color: IPD_COLORS.primary,
        cursor: 'pointer',
        textDecoration: 'underline',
        textDecorationStyle: 'dotted',
        textUnderlineOffset: 3,
        '&:hover': { color: IPD_COLORS.primaryDark },
      }}
    >
      {children}
    </Typography>
  );
}

/** Cosmetic-only reason for a disabled row — backend's can_create_replacement is the real gate. */
function ineligibleReason(row) {
  if (row.po_status === 'CLOSED') return 'PO is closed';
  if (row.po_status === 'CANCELLED') return 'PO is cancelled';
  if (!(Number(row.pending_replacement_qty) > 0)) return 'Fully replaced';
  return 'Not eligible for replacement';
}

// ─── Filter option constants ──────────────────────────────────────────────────

const REPLACEMENT_STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'CREATED', label: 'Created' },
  { value: 'POSTED', label: 'Posted' },
];

// ─── Status badge sizing ──────────────────────────────────────────────────────

/** Static seeds so a column's badge width does not jump once rows load. */
const PO_STATUS_LABELS = [
  'DRAFT', 'PENDING_APPROVAL', 'SUBMITTED', 'APPROVED', 'SENT_TO_VENDOR',
  'PARTIALLY_RECEIVED', 'FULLY_RECEIVED', 'COMPLETED', 'CLOSED', 'CANCELLED',
];
const REPLACEMENT_STATUS_LABELS = ['PENDING', 'CREATED', 'POSTED', 'CANCELLED', 'CLOSED'];

/** Widest label in the set, at the 11px/600 chip font. Same measure as PoList/ReturnList. */
function chipWidthFor(seed, fromData = []) {
  const labels = [...new Set(
    [...seed, ...fromData.filter(Boolean).map(String)].map(s => s.replace(/_/g, ' ')),
  )];
  return Math.ceil(Math.max(...labels.map(l => l.length)) * 8 + 24);
}

const GRN_SEARCH_DEBOUNCE_MS = 3000;
const GRN_SEARCH_MIN_LENGTH = 2;

const PAGE_SIZE = 50;
/** Backend caps query.limit at 200 — same as rest of inventory module. */
const MAX_API_LIMIT = 200;

function buildFilterParams({
  debouncedSearch,
  poFilter,
  vendorFilter,
  itemFilter,
  replacementStatusFilter,
  actionableOnly,
  fromDate,
  toDate,
}) {
  const params = {};
  if (debouncedSearch) params.search = debouncedSearch;
  if (poFilter) params.purchase_order_id = poFilter;
  if (vendorFilter) params.vendor_id = vendorFilter;
  if (itemFilter) params.item_id = itemFilter;
  if (replacementStatusFilter) params.replacement_status = replacementStatusFilter;
  if (actionableOnly) params.actionable_only = true;
  if (fromDate) params.from_date = fromDate;
  if (toDate) params.to_date = toDate;
  return params;
}

function friendlyLoadError(err) {
  if (err?.status === 404) {
    return 'QC rejections API is not available yet. Confirm the backend route is deployed, then retry.';
  }
  return err?.message || 'Failed to load QC rejections';
}

function computeSummary(rows) {
  const totalLines = rows.length;
  const pendingReplacements = rows.filter(r => Number(r.pending_replacement_qty) > 0).length;
  const totalPendingQty = rows.reduce((s, r) => s + (Number(r.pending_replacement_qty) || 0), 0);
  const grnsAffected = new Set(rows.map(r => r.grn_id).filter(Boolean)).size;
  return { totalLines, pendingReplacements, totalPendingQty, grnsAffected };
}

// ─── Dashboard component ──────────────────────────────────────────────────────

export default function RejectedItemsDashboard() {
  const { url } = useRouteMatch();
  const history = useHistory();
  const { can } = useInvRole();
  const base = url.replace('/rejected-items', '');
  const poGrnLookup = usePoGrnLookup();
  const { resolveItemId, getItemCode } = useItemNames();
  const { vendors, resolveVendorName } = useVendorLookup();

  const [rows, setRows] = useState([]);
  const [summaryRows, setSummaryRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [poFilter, setPoFilter] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [itemFilter, setItemFilter] = useState('');
  const [replacementStatusFilter, setReplacementStatusFilter] = useState('');
  const [actionableOnly, setActionableOnly] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  useEffect(() => {
    const trimmed = search.trim();
    if (!trimmed) {
      setDebouncedSearch('');
      return undefined;
    }
    if (trimmed.length < GRN_SEARCH_MIN_LENGTH) {
      setDebouncedSearch('');
      return undefined;
    }
    const t = setTimeout(() => setDebouncedSearch(trimmed), GRN_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, poFilter, vendorFilter, itemFilter, replacementStatusFilter, actionableOnly, fromDate, toDate]);

  const filterParams = useMemo(() => buildFilterParams({
    debouncedSearch,
    poFilter,
    vendorFilter,
    itemFilter,
    replacementStatusFilter,
    actionableOnly,
    fromDate,
    toDate,
  }), [debouncedSearch, poFilter, vendorFilter, itemFilter, replacementStatusFilter, actionableOnly, fromDate, toDate]);

  const loadSummary = useCallback(async () => {
    try {
      const raw = await getQcRejections({
        ...filterParams,
        limit: MAX_API_LIMIT,
        offset: 0,
      });
      setSummaryRows(toInventoryList(raw));
    } catch {
      setSummaryRows([]);
    }
  }, [filterParams]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const raw = await getQcRejections({
        ...filterParams,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      const { items, total } = toInventoryPage(raw, { page, pageSize: PAGE_SIZE });
      setRows(items);
      setTotalCount(total);
    } catch (e) {
      setError(friendlyLoadError(e));
      setRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [filterParams, page]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { load(); }, [load]);

  const getPoId = useCallback((p) => p?.purchase_order_id || p?.id || '', []);
  const getPoLabel = useCallback((p) => {
    if (!p) return '';
    const num = p.po_number || p.purchase_order_id || '';
    const vendor = resolveVendorName(p.vendor_id);
    return vendor && vendor !== '—' ? `${num} — ${vendor}` : num;
  }, [resolveVendorName]);

  const getVendorId = useCallback((v) => v?.vendor_id || v?.id || '', []);
  const getVendorLabel = useCallback((v) => {
    if (!v) return '';
    return v.vendor_code ? `${v.vendor_name} (${v.vendor_code})` : (v.vendor_name || '');
  }, []);

  const grnSearchHelper = (() => {
    const len = search.trim().length;
    if (len > 0 && len < GRN_SEARCH_MIN_LENGTH) {
      return `Type at least ${GRN_SEARCH_MIN_LENGTH} characters`;
    }
    if (len >= GRN_SEARCH_MIN_LENGTH && search.trim() !== debouncedSearch) {
      return 'Searching…';
    }
    return '';
  })();

  const summary = useMemo(() => {
    const source = summaryRows.length > 0 ? summaryRows : rows;
    const computed = computeSummary(source);
    return {
      ...computed,
      totalLines: totalCount || computed.totalLines,
    };
  }, [summaryRows, rows, totalCount]);

  // Fixed chip width from the longest known/data label so every badge in a column aligns.
  // Seeded from the static status list so the width is stable before data lands. Mirrors PoList.
  const poStatusChipMinWidth = useMemo(
    () => chipWidthFor(PO_STATUS_LABELS, rows.map(r => r.po_status)),
    [rows],
  );
  const replacementChipMinWidth = useMemo(
    () => chipWidthFor(REPLACEMENT_STATUS_LABELS, rows.map(r => r.replacement_status)),
    [rows],
  );
  const poStatusColumnWidth = Math.max(140, poStatusChipMinWidth + 28);
  const replacementColumnWidth = Math.max(160, replacementChipMinWidth + 28);

  const hasFilters = Boolean(
    search || poFilter || vendorFilter || itemFilter || replacementStatusFilter || actionableOnly || fromDate || toDate,
  );

  const clearFilters = useCallback(() => {
    setSearch('');
    setPoFilter('');
    setVendorFilter('');
    setItemFilter('');
    setReplacementStatusFilter('');
    setActionableOnly(false);
    setFromDate('');
    setToDate('');
    setPage(0);
  }, []);

  const openReplacementForm = useCallback((row) => {
    history.push(`${base}/rejected-items/replacement-grn/${row.grn_item_id}`, {
      context: {
        ...row,
        item_name: row.item_name || resolveItemId(row.item_id),
        item_code: row.item_code || getItemCode(row.item_id) || undefined,
        vendor_name: row.vendor_name || resolveVendorName(row.vendor_id),
      },
    });
  }, [history, base, resolveItemId, getItemCode, resolveVendorName]);

  const columns = useMemo(() => [
    {
      field: 'grn_number',
      headerName: 'GRN',
      width: 150,
      renderCell: (p) => (
        <LinkText
          disabled={!p.row.grn_id}
          onClick={() => p.row.grn_id && history.push(`${base}/grn/${p.row.grn_id}`)}
        >
          {p.value}
        </LinkText>
      ),
    },
    {
      field: 'grn_date',
      headerName: 'GRN Date',
      width: 110,
      valueFormatter: (p) => formatDate(p.value),
    },
    {
      field: 'po_number',
      headerName: 'PO #',
      width: 140,
      valueGetter: (p) => poGrnLookup.resolvePoNumber(p.row.purchase_order_id, p.row.po_number),
      renderCell: (p) => (
        <LinkText
          disabled={!p.row.purchase_order_id}
          onClick={() => history.push(`${base}/po/${p.row.purchase_order_id}`)}
        >
          {p.value}
        </LinkText>
      ),
    },
    {
      field: 'po_status',
      headerName: 'PO Status',
      width: poStatusColumnWidth,
      renderCell: (p) => (p.value
        ? <StatusChip status={p.value} minWidth={poStatusChipMinWidth} />
        : '—'),
    },
    {
      field: 'item_name',
      headerName: 'Item',
      flex: 1,
      minWidth: 190,
      renderCell: (p) => {
        const name = p.row.item_name || resolveItemId(p.row.item_id);
        const code = p.row.item_code || getItemCode(p.row.item_id);
        return (
          <Box sx={{ py: 0.5 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: IPD_COLORS.textPrimary, lineHeight: 1.4 }}>
              {name}
            </Typography>
            {code && (
              <Typography sx={{ fontSize: 11, color: IPD_COLORS.textSecondary, fontFamily: 'monospace', lineHeight: 1.3 }}>
                {code}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'vendor_name',
      headerName: 'Vendor',
      width: 175,
      valueGetter: (p) => resolveVendorName(p.row.vendor_id) || p.row.vendor_name || '—',
    },
    {
      field: 'rejected_quantity',
      headerName: 'Rejected Qty',
      width: 115,
      align: 'right',
      headerAlign: 'right',
      renderCell: (p) => (
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: IPD_COLORS.error.text }}>
          {formatQty(p.value)}{p.row.uom ? ` ${p.row.uom}` : ''}
        </Typography>
      ),
    },
    {
      field: 'rejection_reason',
      headerName: 'Rejection Reason',
      width: 220,
      renderCell: (p) => (
        <Tooltip title={p.value || ''} placement="top" arrow>
          <Typography sx={{
            fontSize: 12,
            color: IPD_COLORS.textSecondary,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: 200,
          }}>
            {p.value || '—'}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'pending_replacement_qty',
      headerName: 'Pending Qty',
      width: 115,
      align: 'right',
      headerAlign: 'right',
      renderCell: (p) => {
        const pending = Number(p.value) || 0;
        return (
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: pending > 0 ? IPD_COLORS.warning.text : IPD_COLORS.textSecondary }}>
            {formatQty(pending)}
          </Typography>
        );
      },
    },
    {
      field: 'replacement_status',
      headerName: 'Replacement Status',
      width: replacementColumnWidth,
      renderCell: (p) => (p.value
        ? <StatusChip status={p.value} domain="replacement" minWidth={replacementChipMinWidth} />
        : '—'),
    },
    {
      field: 'replacement_grn_number',
      headerName: 'Replacement GRN',
      width: 165,
      renderCell: (p) => {
        if (!p.value) return <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>—</Typography>;
        const linked = poGrnLookup.grns.find(g => g.grn_number === p.value);
        const targetId = p.row.replacement_grn_id || linked?.grn_id || linked?.id;
        return (
          <LinkText disabled={!targetId} onClick={() => history.push(`${base}/grn/${targetId}`)}>
            {p.value}
          </LinkText>
        );
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 170,
      sortable: false,
      renderCell: (p) => {
        const row = p.row;
        if (row.can_create_replacement && can(INV_ACTIONS.REPLACEMENT_GRN_CREATE)) {
          return (
            <InvButton size="small" tone="teal" onClick={() => openReplacementForm(row)}>
              Create Replacement
            </InvButton>
          );
        }
        return (
          <Tooltip title={ineligibleReason(row)}>
            <span>
              <InvButton size="small" tone="teal" disabled>
                Create Replacement
              </InvButton>
            </span>
          </Tooltip>
        );
      },
    },
  ], [history, base, poGrnLookup, openReplacementForm, resolveItemId, getItemCode, resolveVendorName, can,
    poStatusChipMinWidth, poStatusColumnWidth, replacementChipMinWidth, replacementColumnWidth]);

  return (
    <Box>
      <PageHeader
        eyebrow="Material Management"
        title="Rejected Items"
        description={
          loading
            ? 'Loading...'
            : `${summary.totalLines} rejected line${summary.totalLines !== 1 ? 's' : ''} across ${summary.grnsAffected} GRN${summary.grnsAffected !== 1 ? 's' : ''}`
        }
        actions={
          <InvButton
            startIcon={<DownloadIcon />}
            variant="outlined"
            tone="teal"
            size="small"
            onClick={() => exportToCsv('qc-rejections.csv', summaryRows.length ? summaryRows : rows)}
          >
            Export CSV
          </InvButton>
        }
      />


      {/* ── Summary stat cards ─────────────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={6} sm={6} md={3}>
          <StatCard
            label="Total Rejected Lines"
            value={loading ? null : summary.totalLines}
            icon={BlockIcon}
            tone="red"
            loading={loading}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatCard
            label="Pending Replacements"
            value={loading ? null : summary.pendingReplacements}
            icon={PendingActionsIcon}
            tone="amber"
            loading={loading}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatCard
            label="Total Pending Qty"
            value={loading ? null : formatQty(summary.totalPendingQty)}
            icon={WarningAmberIcon}
            tone="amber"
            loading={loading}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatCard
            label="GRNs Affected"
            value={loading ? null : summary.grnsAffected}
            icon={LocalShippingIcon}
            tone="blue"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* ── Filters ───────────────────────────────────────────────────────── */}
      <FilterToolbar sx={{ rowGap: 1.5 }}>
        <FilterToolbar.Search
          placeholder="Search by GRN number..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          helperText={grnSearchHelper}
          FormHelperTextProps={{ sx: { mx: 0, mt: 0.5, fontSize: 11 } }}
        />
        <FilterToolbar.Slot width={200}>
          <InvLookupAutocomplete
            label="Purchase Order"
            options={poGrnLookup.pos}
            value={poFilter}
            onChange={setPoFilter}
            getOptionId={getPoId}
            getOptionLabel={getPoLabel}
            loading={poGrnLookup.loading}
            placeholder="Search PO…"
            noMatchText="No purchase orders"
          />
        </FilterToolbar.Slot>
        <FilterToolbar.Slot width={200}>
          <InvLookupAutocomplete
            label="Vendor"
            options={vendors}
            value={vendorFilter}
            onChange={setVendorFilter}
            getOptionId={getVendorId}
            getOptionLabel={getVendorLabel}
            placeholder="Search vendor…"
            noMatchText="No vendors"
          />
        </FilterToolbar.Slot>
        <FilterToolbar.Slot width={260}>
          <InvItemAutocomplete
            label="Item"
            value={itemFilter}
            onChange={(itemId) => setItemFilter(itemId || '')}
            placeholder="Search item…"
          />
        </FilterToolbar.Slot>
        <FilterToolbar.Filter
          label="Replacement Status"
          value={replacementStatusFilter}
          onChange={(e) => setReplacementStatusFilter(e.target.value)}
          options={REPLACEMENT_STATUS_OPTIONS}
          width={160}
        />
        <FilterToolbar.Slot width={160}>
          <InvTextField
            label="From Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </FilterToolbar.Slot>
        <FilterToolbar.Slot width={160}>
          <InvTextField
            label="To Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </FilterToolbar.Slot>
        <FilterToolbar.Slot>
          <FormControlLabel
            sx={{ ml: 0, color: IPD_COLORS.textSecondary, '& .MuiFormControlLabel-label': { fontSize: 13 } }}
            control={(
              <Switch
                checked={actionableOnly}
                onChange={(e) => setActionableOnly(e.target.checked)}
                sx={switchSx}
              />
            )}
            label="Actionable only"
          />
        </FilterToolbar.Slot>
        {hasFilters && (
          <FilterToolbar.Reset onClick={clearFilters} label="Clear" />
        )}
      </FilterToolbar>

      {/* ── Data grid ─────────────────────────────────────────────────────── */}
      <InvDataGrid
        fillHeight
        rows={rows}
        columns={columns}
        loading={loading}
        error={error}
        onRetry={load}
        getRowId={(r) => r.grn_item_id}
        emptyMessage="No QC rejections found."
        emptyDescription={
          hasFilters
            ? 'Try adjusting or clearing your filters.'
            : 'Items rejected during GRN QC will appear here once GRN is posted to stock.'
        }
        paginationMode="server"
        rowCount={totalCount}
        paginationModel={{ page, pageSize: PAGE_SIZE }}
        onPaginationModelChange={(model) => setPage(model.page)}
        pageSizeOptions={[PAGE_SIZE]}
      />
    </Box>
  );
}
